import React from 'react';

// ─── Field human labels ────────────────────────────────────────────────────────
const FIELD_LABELS = {
  SerialNo:              'Serial No.',
  referenceNumber:       'Reference No.',
  TransactionDate:       'Transaction Date',
  Shipper:               'Shipper',
  ShipperAddress:        'Shipper Address',
  CustomerId:            'Customer',
  CustomerAddress:       'Customer Address',
  Origin:                'Origin',
  Destination:           'Destination',
  Vessel:                'Vessel',
  Voyage:                'Voyage',
  PlateNo:               'Plate No.',
  VanNo:                 'Van No.',
  VanSize:               'Van Size',
  RateCode:              'Rate Code',
  HasWithHolding:        'Has Withholding',
  IsDelivered:           'Is Delivered',
  DeliveryDate:          'Delivery Date',
  notes:                 'Notes',
  Description:           'Description',
  StrippingFee:          'Stripping Fee',
  AdditionalStrippingFee:'Additional Stripping Fee',
  BillName:              'Bill No.',
  BillDate:              'Bill Date',
  BillDueDate:           'Due Date',
  WayBillId:             'Waybill',
  additionalStrippingFee:'Additional Stripping Fee',
  waybillDescription:    'Waybill Description',
  printFreight:          'Print Freight',
  SealNo:                'Seal No.',
  BillingType:           'Billing Type',
  Quantity:              'Quantity',
  Freight:               'Freight',
  Adjustments:           'Adjustments',
  SubTotal:              'SubTotal',
  amountDue:             'Amount Due',
  totalAmount:           'Total Amount',
  linesSync:             'Lines Synced',
  IsReceived:            'Is Received',
};

// Key fields to show for 'created' entries
const CREATED_SHOW_FIELDS = new Set([
  'SerialNo', 'BillName', 'TransactionDate', 'BillDate', 'Shipper', 'CustomerId', 'WayBillId',
]);

function getLabel(field) {
  return FIELD_LABELS[field] || field;
}

const PHP = (n) => new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const DEDUCTION_LABELS = {
  discount: 'Discount', damage: 'Damage', missing: 'Missing', general_expense: 'General Expense',
};
const METHOD_LABELS = {
  cash: 'Cash', check: 'Check', bank_transfer: 'Bank Transfer', gcash: 'GCash', '2307': '2307', other: 'Other',
};

function CollectionChanges({ action, changes }) {
  const c = changes || {};
  const isPayment  = action === 'payment_added'     || action === 'payment_deleted';
  const isDeduct   = action === 'deduction_applied' || action === 'deduction_removed';
  const isRemoved  = action === 'payment_deleted'   || action === 'deduction_removed';

  if (isPayment) {
    return (
      <div className="space-y-0.5">
        <div><span className="font-medium">Amount:</span> ₱{PHP(c.amount)}</div>
        <div><span className="font-medium">Method:</span> {METHOD_LABELS[c.paymentMethod] || c.paymentMethod || '—'}</div>
        {c.referenceNo && <div><span className="font-medium">Ref No.:</span> {c.referenceNo}</div>}
        {isRemoved && <div className="text-red-600 font-medium mt-0.5">Record deleted</div>}
      </div>
    );
  }
  if (isDeduct) {
    return (
      <div className="space-y-0.5">
        <div><span className="font-medium">Amount:</span> ₱{PHP(c.amount)}</div>
        <div><span className="font-medium">Type:</span> {DEDUCTION_LABELS[c.deductionType] || c.deductionType || '—'}</div>
        {c.notes && <div><span className="font-medium">Notes:</span> {c.notes}</div>}
        {isRemoved && <div className="text-red-600 font-medium mt-0.5">Record deleted</div>}
      </div>
    );
  }
  return null;
}



function formatValue(val) {
  if (val === null || val === undefined || val === '') return '—';
  const s = String(val);
  // Boolean strings
  if (s === 'true')  return 'Yes';
  if (s === 'false') return 'No';
  // 24-char hex ObjectId
  if (/^[0-9a-f]{24}$/i.test(s)) return `ID: ${s.slice(0, 8)}…`;
  // ISO date strings
  if (/^\d{4}-\d{2}-\d{2}(T|$)/.test(s)) {
    const d = new Date(s);
    if (!isNaN(d)) return d.toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' });
  }
  return s;
}

function ActionBadge({ action }) {
  const styles = {
    created:           'bg-green-100 text-green-700',
    updated:           'bg-blue-100  text-blue-700',
    voided:            'bg-red-100   text-red-700',
    payment_added:     'bg-teal-100  text-teal-700',
    payment_deleted:   'bg-red-100   text-red-600',
    deduction_applied: 'bg-orange-100 text-orange-700',
    deduction_removed: 'bg-red-100   text-red-600',
    line_added:        'bg-green-100 text-green-700',
    line_updated:      'bg-blue-100  text-blue-700',
    line_deleted:      'bg-red-100   text-red-600',
  };
  const labels = {
    created:           'Created',
    updated:           'Updated',
    voided:            'Voided',
    payment_added:     'Payment Added',
    payment_deleted:   'Payment Deleted',
    deduction_applied: 'Deduction Applied',
    deduction_removed: 'Deduction Removed',
    line_added:        'Line Added',
    line_updated:      'Line Updated',
    line_deleted:      'Line Deleted',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${styles[action] || 'bg-gray-100 text-gray-600'}`}>
      {labels[action] || action}
    </span>
  );
}

export default function AuditTimeline({ entries = [] }) {
  const grouped = entries.reduce((acc, entry) => {
    const dateKey = new Date(entry.createdAt).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
    });
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(entry);
    return acc;
  }, {});

  const dateGroups = Object.entries(grouped);

  return (
    <div className="space-y-6">
      {dateGroups.map(([dateLabel, dayEntries]) => (
        <div key={dateLabel}>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">{dateLabel}</h3>

          <div className="space-y-3">
            {dayEntries.map((entry) => (
              <div key={entry._id} className="border border-gray-200 rounded-lg overflow-hidden">

                {/* Card header */}
                <div className="flex items-center justify-between px-4 py-3 bg-white">
                  <div className="flex items-center gap-3">
                    <ActionBadge action={entry.action} />
                    <span className="text-sm text-gray-500">
                      {new Date(entry.createdAt).toLocaleString('en-PH', {
                        month: 'numeric', day: 'numeric', year: 'numeric',
                        hour: 'numeric', minute: '2-digit', second: '2-digit',
                        hour12: true,
                      })}
                    </span>
                  </div>
                </div>

                {/* By: line */}
                <div className="px-4 py-2 bg-white border-t border-gray-100">
                  <span className="text-sm text-gray-600">
                    By: <span className="font-medium text-gray-800">
                      {entry.performedByName || 'Unknown'}
                    </span>
                  </span>
                </div>

                {/* Changes box */}
                {entry.changes && Object.keys(entry.changes).length > 0 && (
                  <div className="mx-4 mb-4 mt-1 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-xs text-gray-600 space-y-1">

                    {/* Collection events */}
                    {['payment_added','payment_deleted','deduction_applied','deduction_removed'].includes(entry.action) && (
                      <CollectionChanges action={entry.action} changes={entry.changes} />
                    )}

                    {/* CREATED: show snapshot of key fields */}
                    {entry.action === 'created' && (
                      Object.entries(entry.changes)
                        .filter(([k]) => CREATED_SHOW_FIELDS.has(k))
                        .map(([k, v]) => (
                          <div key={k}>
                            <span className="font-medium">{getLabel(k)}:</span>{' '}
                            {formatValue(typeof v === 'object' && v !== null ? JSON.stringify(v) : v)}
                          </div>
                        ))
                    )}

                    {/* UPDATED: show before → after diffs */}
                    {entry.action === 'updated' && (
                      Object.entries(entry.changes).map(([field, val]) => {
                        const before = val?.before;
                        const after  = val?.after;
                        return (
                          <div key={field}>
                            <span className="font-medium">{getLabel(field)}:</span>{' '}
                            <span className="text-red-500 line-through">
                              {formatValue(before)}
                            </span>
                            {' → '}
                            <span className="text-green-600">
                              {formatValue(after)}
                            </span>
                          </div>
                        );
                      })
                    )}

                    {/* VOIDED: show reason */}
                    {entry.action === 'voided' && entry.changes.reason && (
                      <div>
                        <span className="font-medium">Reason:</span>{' '}
                        {entry.changes.reason}
                      </div>
                    )}

                    {/* LINE ADDED / DELETED: show snapshot */}
                    {(entry.action === 'line_added' || entry.action === 'line_deleted') && (() => {
                      const c = entry.changes;
                      const isDeleted = entry.action === 'line_deleted';
                      return (
                        <div className="space-y-0.5">
                          {c.Description && <div><span className="font-medium">Description:</span> {c.Description}</div>}
                          <div><span className="font-medium">Billing Type:</span> {c.BillingType || '—'}</div>
                          <div><span className="font-medium">Qty:</span> {c.Quantity ?? '—'} &nbsp; <span className="font-medium">Freight:</span> ₱{PHP(c.Freight)} &nbsp; <span className="font-medium">Adj:</span> ₱{PHP(c.Adjustments)}</div>
                          <div><span className="font-medium">SubTotal:</span> ₱{PHP(c.SubTotal)}</div>
                          {(c.VanNo || c.SealNo) && <div><span className="font-medium">Van:</span> {c.VanNo || '—'} &nbsp; <span className="font-medium">Seal:</span> {c.SealNo || '—'}</div>}
                          {isDeleted && <div className="text-red-600 font-medium mt-0.5">Line removed</div>}
                        </div>
                      );
                    })()}

                    {/* LINE UPDATED: show before → after diffs */}
                    {entry.action === 'line_updated' && (
                      Object.entries(entry.changes).map(([field, val]) => (
                        <div key={field}>
                          <span className="font-medium">{getLabel(field)}:</span>{' '}
                          <span className="text-red-500 line-through">{formatValue(val?.before)}</span>
                          {' → '}
                          <span className="text-green-600">{formatValue(val?.after)}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
